import {
  STORE_STATUS_FINISHED,
  STORE_STATUS_INITIALIZED,
  STORE_TUTORIAL,
  STORE_TUTORIAL_FETCH_LIST,
} from 'constants/store'
import {
  createGenericSlice,
  createReducers,
  ExtraReducers,
  GenericState,
} from 'stores/generics'
import { ErrorStore, TutorialListResponse } from '@types'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { Payload } from 'stores'
import { PayloadAction } from '@reduxjs/toolkit'

export type TutorialsDone = string

export type TutorialState = {
  done: TutorialsDone[]
  queue: TutorialsDone[]
  current?: TutorialsDone
}
type State = GenericState<TutorialState>

export const initialState: State = {
  data: {
    done: [],
    queue: [],
    current: undefined,
  },
  status: STORE_STATUS_INITIALIZED,
  error: null,
}

export const fetchList = createAsyncThunk<
  TutorialState,
  Promise<TutorialListResponse>,
  Payload
>(
  STORE_TUTORIAL_FETCH_LIST,
  async (promise: Promise<any>, { getState, rejectWithValue }) => {
    try {
      const response = await promise
      const data = getState().tutorial.list.data
      const queue = difference(data.queue, response.tutorials)
      const current = data.current || queue[0]
      return {
        ...data,
        done: response.tutorials,
        ...(setCurrent(response.tutorials, current) && { current }),
        queue,
      }
    } catch (e: unknown) {
      const { code, message } = e as ErrorStore
      return rejectWithValue({ code, message })
    }
  }
)

const extraReducers: ExtraReducers = createReducers<
  TutorialState,
  TutorialListResponse,
  Payload
>(fetchList, initialState.data)

const listSlice = createGenericSlice({
  name: STORE_TUTORIAL,
  initialState,
  reducers: {
    registerTutorial: (
      state,
      action: PayloadAction<{
        id: string
        tutorialsConfig: Record<string, any>
      }>
    ) => {
      // se não for um novo tutorial não faz nada"
      if (
        state.data.done.includes(action.payload.id) ||
        state.data.queue.includes(action.payload.id)
      ) {
        return state
      }

      // adiciona novo tutorial, reordena por ordem de relevância e seta o tutorial atual
      return {
        ...state,
        data: {
          ...state.data,
          queue: reOrderByRelevancy(
            [...state.data.queue, action.payload.id],
            action.payload.tutorialsConfig
          ),
          current: state.data.current
            ? state.data.current
            : setCurrent(state.data.done, action.payload.id),
        },
        status: STORE_STATUS_FINISHED,
      }
    },
    removeFromQueue: (state, action: PayloadAction<string>) => {
      const payloadIndex = state.data.queue.indexOf(action.payload)
      const queue = [...state.data.queue]
      queue.splice(payloadIndex, 1)
      return {
        ...state,
        data: {
          ...state.data,
          queue,
        },
        status: STORE_STATUS_FINISHED,
      }
    },
  },
  extraReducers,
})

function reOrderByRelevancy(
  queue: string[],
  texts: Record<string, any>
): string[] {
  return [...queue].sort((a, b) => {
    const x = texts[b].relevancy || 1
    const y = texts[a].relevancy || 1
    return x < y ? -1 : x > y ? 1 : 0
  })
}

function difference(a: string[], b: string[]) {
  return a.filter((a) => !b.includes(a))
}

function setCurrent(tutorialsDone: string[], current?: string) {
  return current && !tutorialsDone.includes(current) ? current : undefined
}

const { actions, reducer } = listSlice

const { clear, success, registerTutorial, removeFromQueue } = actions

export { clear, success, registerTutorial, removeFromQueue, setCurrent }

export default reducer
